import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import { List } from "@/components/list/List";
import Ellipsis from "@/components/views/Ellipsis";
import HtmlView from "@/components/views/HtmlView";
import { formatLocaleDateTime } from "@/utils/formatUtils";
import { isRecent } from "@/utils/utils";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { MenuInfo } from "@rc-component/menu/es/interface";
import { App as AntApp, Badge, Button, Card, Drawer, Dropdown, Tooltip } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  useDeleteDashboardNotice,
  useInfiniteFilterDashboardNotices,
  useMarkAllAsSeenDashboardNotices,
  useToggleSeenDashboardNotice,
} from "../../../../../common/api/queries";
import { DrawerSizes } from "../../../../../common/constants";
import { Permission } from "../../../../../common/security/enums";
import { useHasPermissions } from "../../../../auth/hooks";
import { DashboardNotice } from "../../../types";
import { DashboardNoticeForm } from "../../forms";
import {
  DashboardNoticeAttachments,
  DashboardNoticeClosedTag,
  DashboardNoticeIcon,
  DashboardNoticePositionTag,
} from "./components";

const ACTION = {
  UPDATE: "UPDATE",
  DELETE: "DELETE",
  MARK_AS_UNSEEN: "MARK_AS_UNSEEN",
};

type Props = {
  isOpen: boolean;
  onCancel: VoidFunction;
};

export const DashboardNoticesWidget = ({ isOpen, onCancel }: Props) => {
  const { t } = useTranslation();
  const { modal } = AntApp.useApp();

  const [noticeFormOpen, setNoticeFormOpen] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState<DashboardNotice>();

  const hasAdminDashboardPermission = useHasPermissions(Permission.ADMIN_DASHBOARD);

  const markAllAsSeen = useMarkAllAsSeenDashboardNotices();
  const toggleSeen = useToggleSeenDashboardNotice();
  const { data, isFetchingNextPage, fetchNextPage, hasNextPage, isFetching } = useInfiniteFilterDashboardNotices();
  const notices = data?.pages.flatMap((page) => page.pageData);

  const deleteDashboardNotice = useDeleteDashboardNotice();

  useEffect(() => {
    if (isOpen && notices) {
      markAllAsSeen.mutate();
    }
  }, [isOpen]);

  const isNew = (notice: DashboardNotice) => !notice.seenAt || isRecent(notice.seenAt);

  const handleContactFormCancel = () => {
    setSelectedNotice(undefined);
    setNoticeFormOpen(false);
  };

  const handleMenuDropdownClick = (clickInfo: MenuInfo, notice: DashboardNotice) => {
    switch (clickInfo.key) {
      case ACTION.UPDATE:
        setSelectedNotice(notice);
        setNoticeFormOpen(true);
        break;
      case ACTION.DELETE:
        modal.confirm({
          title: t("dashboard.notices.helpers.deleteConfirm"),
          icon: <ExclamationCircleOutlined />,
          okText: t("common.yes"),
          cancelText: t("common.no"),
          okType: "danger",
          onOk: () => {
            deleteDashboardNotice.mutate(notice.id);
          },
        });
        break;
      case ACTION.MARK_AS_UNSEEN:
        toggleSeen.mutate(notice.id);
        break;
    }
  };

  return (
    <Drawer
      title={t("dashboard.notices.title")}
      open={isOpen}
      onClose={onCancel}
      size={DrawerSizes.SMALL}
      extra={
        <>
          {hasAdminDashboardPermission && (
            <Button
              style={{ marginLeft: 16 }}
              type="primary"
              icon={<AntIcon type="plus" />}
              onClick={() => setNoticeFormOpen(true)}
            >
              {t("common.add")}
            </Button>
          )}
        </>
      }
      styles={{ body: { padding: 0 } }}
    >
      <div id="dashboard-notice-scroll" style={{ overflow: "auto", height: "100%", paddingTop: 12 }}>
        <InfiniteScroll
          dataLength={(data?.pages.length ?? 0) * (data?.pages[0]?.pageSize ?? 0)}
          next={() => {
            if (!isFetchingNextPage) {
              fetchNextPage();
            }
          }}
          hasMore={hasNextPage}
          loader="..."
          scrollableTarget="dashboard-notice-scroll"
        >
          <List<DashboardNotice>
            loading={isFetching}
            gap={24}
            showEmpty
            style={{ paddingLeft: 24, paddingRight: 16 }}
            items={notices ?? []}
            rowKey={(item) => item.id}
            renderItem={(item) => (
              <Badge.Ribbon
                style={{
                  display: isNew(item) ? "inherit" : "none",
                }}
                text={t("common.new")}
                placement="start"
              >
                <Card
                  style={{ borderRadius: 18 }}
                  styles={{
                    actions: { borderBottomLeftRadius: 18, borderBottomRightRadius: 18 },
                    body: {
                      backgroundColor: isNew(item) ? "#e6f4ff" : "white",
                      borderRadius: 18,
                    },
                  }}
                  actions={
                    item.attachments.length > 0
                      ? [<DashboardNoticeAttachments noticeId={item.id} attachments={item.attachments} />]
                      : undefined
                  }
                >
                  <Card.Meta
                    style={{ paddingRight: 24 }}
                    title={<Ellipsis style={{ fontSize: 16 }}>{item.title}</Ellipsis>}
                    avatar={
                      <Tooltip title={t(`dashboard.notices.enums.type.${item.type}`)}>
                        <span>
                          <DashboardNoticeIcon type={item.type} />
                        </span>
                      </Tooltip>
                    }
                    description={
                      <>
                        <Tooltip title={formatLocaleDateTime(item.createdAt)}>
                          {dayjs(item.createdAt).fromNow()}
                        </Tooltip>

                        {item.closed ? <DashboardNoticeClosedTag style={{ marginLeft: 8 }} /> : undefined}
                      </>
                    }
                  />

                  <div style={{ padding: "24px 0 18px" }}>
                    <HtmlView value={item.text} />
                  </div>

                  {item.positions?.length > 0 ? (
                    <div className="margin-bottom-tiny">
                      <span style={{ paddingRight: 8 }}>{t("dashboard.notices.enums.position._label")}:</span>

                      {item.positions.map((type) => (
                        <DashboardNoticePositionTag key={type} position={type} />
                      ))}
                    </div>
                  ) : undefined}

                  <div style={{ position: "absolute", right: 16, top: 16 }}>
                    {hasAdminDashboardPermission ? (
                      <Dropdown
                        trigger={["click"]}
                        placement="bottomRight"
                        menu={{
                          items: [
                            isNew(item)
                              ? null
                              : {
                                  key: ACTION.MARK_AS_UNSEEN,
                                  label: (
                                    <ActionTextIcon
                                      icon="eye-invisible"
                                      color="blue"
                                      text={t("dashboard.notices.actions.markAsUnseen")}
                                    />
                                  ),
                                },
                            {
                              key: ACTION.UPDATE,
                              label: <ActionTextIcon icon="edit" color="blue" text={t("common.edit")} />,
                            },
                            {
                              key: ACTION.DELETE,
                              label: <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />,
                            },
                          ],
                          onClick: (clickInfo) => handleMenuDropdownClick(clickInfo, item),
                        }}
                      >
                        <Button type="text" icon={<AntIcon type="more" />} />
                      </Dropdown>
                    ) : (
                      item.seenAt && (
                        <Tooltip title={t("dashboard.notices.actions.markAsUnseen")}>
                          <Button
                            type="link"
                            icon={<AntIcon type="eye-invisible" />}
                            onClick={() => toggleSeen.mutate(item.id)}
                          ></Button>
                        </Tooltip>
                      )
                    )}
                  </div>
                </Card>
              </Badge.Ribbon>
            )}
          />
        </InfiniteScroll>
      </div>

      <DashboardNoticeForm notice={selectedNotice} open={noticeFormOpen} onFormCancel={handleContactFormCancel} />
    </Drawer>
  );
};
